import { useState, useEffect } from 'react';
import { Flight } from '../types/Quotation';
import { fetchFlights } from '../api/Flights';
import { TravellerInfo } from '../types/Quotation';

interface UseFlightsResult {
  flights: Flight[];
  loading: boolean;
  error: Error | null;
}

export function useFlights(
  from: string,
  to: string,
  date: string,
  travellers?: TravellerInfo,
  cabinClass?: number
): UseFlightsResult {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!from || !to || !date) {
      setFlights([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchFlights(from, to, date, travellers, cabinClass)
      .then(data => {
        if (cancelled) return;
        const items: any[] = (data && (data as any).items) || [];
        const normalized: Flight[] = items.map((f: any) => {
          const segs: any[] = Array.isArray(f.segments) ? f.segments : [];
          const first = segs[0] || {};
          const provider = f.provider || first.airlineCode || '';
          const airline = f.airline || first.airline || provider;
          const flightNumberJoined = f.flightNumber || (segs.length ? segs.map((s: any) => s.flightNumber).join('·') : '');
          const flight: any = {
            airline,
            flightNumber: String(flightNumberJoined || ''),
            from: f.from || first.fromCode || first.origin || '',
            to: f.to || first.toCode || first.destination || '',
            departureTime: f.departureTime,
            arrivalTime: f.arrivalTime,
            duration: f.duration,
            stops: typeof f.stops === 'number' ? f.stops : Math.max(0, (segs.length || 1) - 1),
            price: Number(f.price || 0),
            provider,
            class: f.class,
          } as Flight;
          (flight as any).segments = segs;
          return flight;
        });
        setFlights(normalized);
      })
      .catch(err => {
        if (!cancelled) setError(err as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [from, to, date, travellers, cabinClass]);

  return { flights, loading, error };
}
